import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { testPrompt, executePrompt, getAvailableModels } from '../../../../../services/aiOperationsApi';

type ActiveTab = 'preview' | 'execute';

interface AvailableModel {
  id: string;
  name: string;
  provider: string;
}

interface TestResult {
  resolved_template: string;
}

interface ExecutionResult {
  success: boolean;
  model_id: string;
  input_tokens?: number;
  output_tokens?: number;
  cost_usd?: number;
  latency_ms?: number;
  response_text?: string;
  error_message?: string;
}

interface PromptItem {
  id: string;
  name: string;
  template?: string;
  version_number?: number;
  environment?: string;
}

interface TestPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: PromptItem | null;
}

export const TestPromptModal: React.FC<TestPromptModalProps> = ({
  isOpen,
  onClose,
  prompt,
}) => {
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TestResult | null>(null);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);

  const [maxTokens, setMaxTokens] = useState(1024);
  const [temperature, setTemperature] = useState(0.7);
  const [showSettings, setShowSettings] = useState(false);

  const [activeTab, setActiveTab] = useState<ActiveTab>('preview');

  const templateVars = useMemo(() => {
    if (!prompt?.template) return [];
    const regex = /\{\{(\w+)\}\}/g;
    const vars: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = regex.exec(prompt.template)) !== null) {
      if (!vars.includes(match[1])) {
        vars.push(match[1]);
      }
    }
    return vars;
  }, [prompt?.template]);

  useEffect(() => {
    if (isOpen) {
      loadModels();
      setVariables({});
      setResult(null);
      setExecutionResult(null);
      setError(null);
      setActiveTab('preview');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await getAvailableModels();
      setAvailableModels(response.models ?? []);
      if ((response.models?.length ?? 0) > 0) {
        setSelectedModel(response.models[0].id);
      }
    } catch (err: unknown) {
      console.error('Failed to load models:', err);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleVariableChange = (varName: string, value: string) => {
    setVariables(prev => ({ ...prev, [varName]: value }));
  };

  const handleTest = async () => {
    if (!prompt) return;
    setLoading(true);
    setError(null);
    try {
      const response = await testPrompt(prompt.id, variables);
      setResult(response);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to test prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!prompt) return;
    if (!selectedModel) {
      setError('Please select a model to execute the prompt');
      return;
    }

    setExecuting(true);
    setError(null);
    setExecutionResult(null);

    try {
      const response = await executePrompt(prompt.id, {
        variables,
        model_id: selectedModel,
        max_tokens: maxTokens,
        temperature,
        save_to_history: true,
      });
      setExecutionResult(response);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to execute prompt');
    } finally {
      setExecuting(false);
    }
  };

  const handleClose = () => {
    setVariables({});
    setResult(null);
    setExecutionResult(null);
    setError(null);
    onClose();
  };

  const formatCost = (cost: number | undefined): string => {
    if (!cost || cost === 0) return '$0.00';
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    return `$${cost.toFixed(4)}`;
  };

  const formatLatency = (ms: number | undefined): string => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  if (!prompt) return null;

  const hasModels = availableModels.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Test Prompt" maxWidth="max-w-4xl">
      <div className="space-y-6">
        {/* Prompt Info */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">
            {prompt.name}
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Version {prompt.version_number ?? 1} &bull; {prompt.environment ?? 'development'}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('preview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'preview'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Preview Template
            </button>
            <button
              onClick={() => setActiveTab('execute')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'execute'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Execute with LLM
              {hasModels && (
                <span className="px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                  {availableModels.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Template Variables Form */}
        {templateVars.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Template Variables
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {templateVars.length} variable{templateVars.length === 1 ? '' : 's'} detected
              </span>
            </div>
            <div className="grid gap-4">
              {templateVars.map(varName => (
                <div key={varName}>
                  <label htmlFor="varname" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-blue-600 dark:text-blue-400">
                      {`{{${varName}}}`}
                    </code>
                  </label>
                  <textarea id="varname"
                    value={variables[varName] ?? ''}
                    onChange={(e) => handleVariableChange(varName, e.target.value)}
                    placeholder={`Enter value for ${varName}...`}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent
                             placeholder-gray-400 dark:placeholder-gray-500 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center">
            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No template variables detected in this prompt.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Variables use the format: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{`{{variable_name}}`}</code>
            </p>
          </div>
        )}

        {/* Execute Tab: Model Selection */}
        {activeTab === 'execute' && (
          <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            {loadingModels ? (
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading available models...
              </div>
            ) : hasModels ? (
              <>
                <div>
                  <label htmlFor="select-model" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Model
                  </label>
                  <select id="select-model"
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    {availableModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} ({model.provider})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <svg className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Advanced Settings
                </button>

                {showSettings && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <label htmlFor="max-tokens" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Tokens
                      </label>
                      <input id="max-tokens"
                        type="number"
                        value={maxTokens}
                        onChange={(e) => setMaxTokens(Number.parseInt(e.target.value) || 1024)}
                        min={1}
                        max={4096}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="temperature-temperature" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Temperature ({temperature})
                      </label>
                      <input id="temperature-temperature"
                        type="range"
                        value={temperature}
                        onChange={(e) => setTemperature(Number.parseFloat(e.target.value))}
                        min={0}
                        max={1}
                        step={0.1}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <svg className="w-10 h-10 text-blue-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                  No LLM providers configured
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Go to <strong>Settings &gt; LLM Providers</strong> to add your API credentials
                </p>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Preview Result Section */}
        {activeTab === 'preview' && result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Resolved Template
              </h4>
            </div>
            <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                {result.resolved_template}
              </pre>
            </div>
          </div>
        )}

        {/* Execution Result Section */}
        {activeTab === 'execute' && executionResult && (
          <div className="space-y-4">
            <div className={`rounded-lg p-4 ${
              executionResult.success
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {executionResult.success ? (
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <span className={`font-medium ${
                    executionResult.success
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    {executionResult.success ? 'Execution Successful' : 'Execution Failed'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {executionResult.model_id}
                  </span>
                </div>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Input Tokens</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {executionResult.input_tokens?.toLocaleString() ?? 0}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Output Tokens</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {executionResult.output_tokens?.toLocaleString() ?? 0}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cost</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCost(executionResult.cost_usd)}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Latency</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatLatency(executionResult.latency_ms)}
                </p>
              </div>
            </div>

            {executionResult.response_text && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Response
                </h4>
                <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 overflow-x-auto max-h-96">
                  <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                    {executionResult.response_text}
                  </pre>
                </div>
              </div>
            )}

            {executionResult.error_message && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">{executionResult.error_message}</p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={handleClose} disabled={loading || executing}>
            Close
          </Button>
          {activeTab === 'preview' ? (
            <Button variant="primary" onClick={handleTest} disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Resolving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </span>
              )}
            </Button>
          ) : (
            <Button variant="primary" onClick={handleExecute} disabled={executing || !hasModels || !selectedModel}>
              {executing ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Executing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Execute
                </span>
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TestPromptModal;
